// LazyRouteBoundary — ловит ChunkLoadError от React.lazy и показывает
// offline-friendly UI. Offline-first UX plan фаза 0.
//
// Починка — только перезагрузка документа (`recoverFromChunkLoad`), не ремаунт
// поддерева: отказ загрузки модуля кэшируется и в module map браузера, и в
// самом `React.lazy`, поэтому повторный рендер тех же children достаёт ту же
// ошибку мгновенно и без сети. Разбор — `lib/chunk-load.ts`.

import { Button } from "@studyhub/ui/components/button";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { Component, type ErrorInfo, type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOnlineStatus } from "@/hooks/use-online-status";
import {
	isChunkLoadError,
	recoverFromChunkLoad,
	shouldAutoRecoverFromChunkLoad,
} from "@/lib/chunk-load";
import { captureClientError } from "@/lib/observability/hawk-client";

interface Props {
	children: ReactNode;
}

interface State {
	error: Error | null;
}

/**
 * Экран «не смогли загрузить модуль страницы». Экспортируется, потому что тот
 * же случай ловит и `RouteErrorComponent` (ошибка загрузчика роута): копия
 * текстов и кнопки в двух местах разъехалась бы.
 */
export function ChunkLoadFallback({
	online,
	onRetry,
}: {
	online: boolean;
	onRetry: () => void;
}): React.ReactElement {
	const { t } = useTranslation("common");

	// Экран — не конечная остановка: онлайн мёртвый чанк чинится новым
	// документом, и ждать для этого нажатия незачем. Решение одно на обе
	// поверхности (`lib/chunk-load.ts`), окно остывания внутри него же не даёт
	// зациклиться, если чанк мёртв по-настоящему.
	useEffect(() => {
		if (shouldAutoRecoverFromChunkLoad(online)) onRetry();
	}, [online, onRetry]);

	return (
		<div className="flex min-h-[50dvh] items-center justify-center px-4">
			<ErrorState
				title={t("errors.chunkLoad.title", {
					defaultValue: "Couldn't load this screen",
				})}
				description={
					online
						? t("errors.chunkLoad.bodyOnline", {
								defaultValue: "Failed to load the page module. Try again.",
							})
						: t("errors.chunkLoad.bodyOffline", {
								defaultValue:
									"This section isn't available offline yet. We'll retry when you're back online.",
							})
				}
				action={
					<Button variant="outline" className="cursor-pointer" onClick={onRetry}>
						{t("errors.chunkLoad.retry", { defaultValue: "Try again" })}
					</Button>
				}
			/>
		</div>
	);
}

class LazyRouteBoundaryInner extends Component<Props & { online: boolean }, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): Partial<State> {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		captureClientError(error, {
			source: "lazy-route-boundary",
			componentStack: info.componentStack ?? undefined,
			chunkLoad: isChunkLoadError(error),
		});
	}

	render(): ReactNode {
		const { error } = this.state;
		if (error) {
			// Не-ChunkLoadError — пробрасываем в GlobalErrorBoundary.
			if (!isChunkLoadError(error)) {
				throw error;
			}
			return <ChunkLoadFallback online={this.props.online} onRetry={recoverFromChunkLoad} />;
		}
		// Обёртка осталась от прежнего сброса по ключу: раскладка трёх роутов
		// (материал, конспект, чат) выстроена уже с ней, и снятие элемента —
		// отдельная правка вёрстки, не этой.
		return <div>{this.props.children}</div>;
	}
}

/** Functional wrapper — даёт online status в class boundary. */
export function LazyRouteBoundary({ children }: Props): React.ReactElement {
	const online = useOnlineStatus();
	return <LazyRouteBoundaryInner online={online}>{children}</LazyRouteBoundaryInner>;
}
